import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { L as Link } from "../lib/i18n";
import { api } from "../lib/api";
import { getModelsForBrand } from "../lib/brandModels";
import {
  BRANDS, FUELS, TRANSMISSIONS, FEATURE_CATEGORIES, CONDITIONS, SELLER_TYPES,
  EURO_NORMS, DRIVETRAINS, BODY_TYPES, DOOR_OPTIONS, SEAT_OPTIONS, CYLINDER_OPTIONS,
  EXTERIOR_COLORS, INTERIOR_COLORS,
} from "../lib/constants";
import { useLang } from "../contexts/LanguageContext";
import { useActiveLocale } from "../lib/i18n";
import CarCard from "../components/CarCard";
import RecentlyViewed from "../components/RecentlyViewed";
import JsonLd from "../components/JsonLd";
import { useSeoHead } from "../lib/seoHead";
import { getPageSeo, buildHreflangs, buildBreadcrumbList, buildCollectionPageSchema } from "../lib/pageSeo";
import { buildSeoListingsItemList } from "../lib/schema";
import { trackEvent } from "../lib/analytics";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "../components/ui/sheet";
import { Filter, X, BellPlus, SlidersHorizontal, ChevronDown, Check } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import MobileQuickFilters from "../components/MobileQuickFilters";

// All filter keys we know about (used for chip-rendering & buildQuery)
const STR_KEYS = ["brand", "model", "fuel", "transmission", "q", "sort", "features", "condition",
  "seller_type", "euro_norm", "drivetrain", "body_type", "exterior_color", "interior_color"];
const NUM_KEYS = ["min_price", "max_price", "max_km", "min_year", "min_warranty",
  "min_power_hp", "max_power_hp", "doors", "seats", "cylinders",
  "min_displacement", "max_displacement",
  "max_fuel_consumption", "max_co2", "min_battery", "min_electric_range"];
// Phase 68.3 — added ab_mfk, accident_free, warranty_available, service_book
const BOOL_KEYS = ["metallic", "verified_dealer", "ab_mfk", "accident_free", "warranty_available", "service_book"];

const SITE_URL = (process.env.REACT_APP_PUBLIC_URL || "").replace(/\/$/, "");
const FILE_BASE_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export default function Listings() {
  const { t } = useLang();
  const locale = useActiveLocale();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [savingSearch, setSavingSearch] = useState(false);
  // Phase 68.3 — controlled mobile drawer state (fixes "Fahrzeuge anzeigen" not closing)
  const [mobileOpen, setMobileOpen] = useState(false);
  // Phase 68.4 — reusable save-search handler (used by quick-actions + header alert btn)
  const handleSaveSearch = React.useCallback(async () => {
    if (!user) { toast.error("Bitte zuerst anmelden"); return; }
    const filters = buildQuery();
    if (Object.keys(filters).length === 0) { toast.error("Bitte mindestens einen Filter setzen"); return; }
    const name = window.prompt("Name für diese Suche:", filters.brand || filters.q || "Meine Suche");
    if (!name) return;
    setSavingSearch(true);
    try { await api.createSearch({ name, filters, notify: true }); toast.success("Suche gespeichert"); }
    catch { toast.error("Fehler"); } finally { setSavingSearch(false); }
  }, [user, params]);

  const get = (k) => params.get(k) || "";
  const setMany = (updates) => {
    const next = new URLSearchParams(params);
    Object.entries(updates).forEach(([k, v]) => {
      if (v === "" || v == null) next.delete(k);
      else next.set(k, v);
    });
    setParams(next);
  };
  const buildQuery = () => {
    const out = {};
    STR_KEYS.forEach((k) => { if (get(k)) out[k] = get(k); });
    NUM_KEYS.forEach((k) => { if (get(k)) out[k] = Number(get(k)); });
    BOOL_KEYS.forEach((k) => { if (get(k) === "1" || get(k) === "true") out[k] = true; });
    return out;
  };
  const selectedFeatures = (get("features") || "").split(",").filter(Boolean);
  const toggleFeature = (name) => {
    const set = new Set(selectedFeatures);
    set.has(name) ? set.delete(name) : set.add(name);
    setMany({ features: Array.from(set).join(",") });
  };

  // build active chips
  const chips = [];
  ["brand", "fuel", "transmission", "condition", "seller_type", "euro_norm", "drivetrain", "body_type", "q"]
    .forEach((k) => { if (get(k)) chips.push({ k, label: get(k) }); });
  if (get("min_price") || get("max_price"))
    chips.push({ k: "_price", label: `CHF ${get("min_price") || 0}–${get("max_price") || "∞"}`, clear: () => setMany({ min_price: "", max_price: "" }) });
  if (get("min_power_hp") || get("max_power_hp"))
    chips.push({ k: "_power", label: `${get("min_power_hp") || 0}–${get("max_power_hp") || "∞"} PS`, clear: () => setMany({ min_power_hp: "", max_power_hp: "" }) });
  if (get("min_displacement") || get("max_displacement"))
    chips.push({ k: "_disp", label: `${get("min_displacement") || 0}–${get("max_displacement") || "∞"} cc`, clear: () => setMany({ min_displacement: "", max_displacement: "" }) });
  if (get("max_km")) chips.push({ k: "max_km", label: `≤ ${get("max_km")} km` });
  if (get("min_year")) chips.push({ k: "min_year", label: `ab ${get("min_year")}` });
  if (get("verified_dealer") === "true") chips.push({ k: "verified_dealer", label: "✓ Verifizierte Händler" });
  if (get("min_warranty")) chips.push({ k: "min_warranty", label: `${get("min_warranty")}M ${t.filters.warranty}` });
  if (get("doors")) chips.push({ k: "doors", label: `${get("doors")} ${t.filters.doors}` });
  if (get("seats")) chips.push({ k: "seats", label: `${get("seats")} ${t.filters.seats}` });
  if (get("cylinders")) chips.push({ k: "cylinders", label: `${get("cylinders")} ${t.filters.cylinders}` });
  if (get("max_fuel_consumption")) chips.push({ k: "max_fuel_consumption", label: `≤ ${get("max_fuel_consumption")} l/100km` });
  if (get("max_co2")) chips.push({ k: "max_co2", label: `≤ ${get("max_co2")} CO₂` });
  if (get("min_battery")) chips.push({ k: "min_battery", label: `≥ ${get("min_battery")} kWh` });
  if (get("min_electric_range")) chips.push({ k: "min_electric_range", label: `≥ ${get("min_electric_range")} km el.` });
  if (get("exterior_color")) chips.push({ k: "exterior_color", label: get("exterior_color") });
  if (get("interior_color")) chips.push({ k: "interior_color", label: `Innen: ${get("interior_color")}` });
  if (get("metallic") === "1") chips.push({ k: "metallic", label: t.filters.metallic, clear: () => setMany({ metallic: "" }) });
  // Phase 68.3 — boolean attribute chips
  if (get("ab_mfk") === "1") chips.push({ k: "ab_mfk", label: "Ab MFK", clear: () => setMany({ ab_mfk: "" }) });
  if (get("accident_free") === "1") chips.push({ k: "accident_free", label: "Unfallfrei", clear: () => setMany({ accident_free: "" }) });
  if (get("warranty_available") === "1") chips.push({ k: "warranty_available", label: "Garantie", clear: () => setMany({ warranty_available: "" }) });
  if (get("service_book") === "1") chips.push({ k: "service_book", label: "Service-Heft", clear: () => setMany({ service_book: "" }) });
  selectedFeatures.forEach((f) => chips.push({ k: `feature:${f}`, label: f, clear: () => toggleFeature(f) }));

  useEffect(() => {
    setLoading(true);
    api.listListings({ ...buildQuery(), limit: 24 })
      .then((d) => { setItems(d.items); setTotal(d.total); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const filtersBlock = (
    <div className="space-y-2 pb-32 lg:pb-0" data-testid="filters-panel">
      <div className="flex items-center justify-between border-b border-[var(--ch-line)] pb-3">
        <h2 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-300">
          <Filter className="h-4 w-4" /> {t.filters.title}
          {chips.length > 0 && (
            <span className="ml-1 rounded-full bg-[var(--ch-red)] px-1.5 py-0.5 text-[10px] font-bold text-white" data-testid="filter-active-count">
              {chips.length}
            </span>
          )}
        </h2>
        <button onClick={() => setParams({})} className="text-xs text-zinc-500 transition-colors hover:text-white" data-testid="reset-filters">
          <X className="inline h-3 w-3" /> {t.filters.reset}
        </button>
      </div>

      {/* Basics */}
      <Section title={t.filters.basics} defaultOpen>
        <FilterField label={t.filters.brand}>
          <Select value={get("brand") || "any"} onValueChange={(v) => setMany({ brand: v === "any" ? "" : v, model: "" })}>
            <SelectTrigger data-testid="filter-brand" className="border-[var(--ch-line)] bg-[var(--ch-bg-1)] text-white"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72 border-[var(--ch-line)] bg-[var(--ch-bg-1)] text-white">
              <SelectItem value="any">{t.filters.any}</SelectItem>
              {BRANDS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
            </SelectContent>
          </Select>
        </FilterField>

        {/* Phase 55 — Model cascade: only renders once a brand is selected. */}
        {get("brand") && (() => {
          const brandModels = getModelsForBrand(get("brand"));
          const cur = get("model");
          const known = !cur || brandModels.includes(cur);
          // Brand without curated model list → free-text input fallback.
          if (brandModels.length === 0) {
            return (
              <FilterField label={t.filters.model || "Modell"}>
                <input
                  value={cur}
                  onChange={(e) => setMany({ model: e.target.value })}
                  placeholder={`${t.filters.any} - ${get("brand")}`}
                  className="w-full rounded-md border border-[var(--ch-line)] bg-[var(--ch-bg-1)] px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:border-red-600 focus:outline-none"
                  data-testid="filter-model-free"
                />
              </FilterField>
            );
          }
          return (
            <FilterField label={t.filters.model || "Modell"}>
              <Select
                value={known ? (cur || "any") : "__custom__"}
                onValueChange={(v) => setMany({ model: v === "any" || v === "__custom__" ? "" : v })}
              >
                <SelectTrigger data-testid="filter-model" className="border-[var(--ch-line)] bg-[var(--ch-bg-1)] text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72 border-[var(--ch-line)] bg-[var(--ch-bg-1)] text-white">
                  <SelectItem value="any">{t.filters.any}</SelectItem>
                  {brandModels.map((m) => (
                    <SelectItem key={m} value={m} data-testid={`filter-model-${m}`}>{m}</SelectItem>
                  ))}
                  {!known && (
                    <SelectItem value="__custom__" disabled>
                      {cur} (custom)
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </FilterField>
          );
        })()}

        <FilterField label={t.filters.body}>
          <Select value={get("body_type") || "any"} onValueChange={(v) => setMany({ body_type: v === "any" ? "" : v })}>
            <SelectTrigger className="border-[var(--ch-line)] bg-[var(--ch-bg-1)] capitalize text-white" data-testid="filter-body"><SelectValue /></SelectTrigger>
            <SelectContent className="border-[var(--ch-line)] bg-[var(--ch-bg-1)] text-white">
              <SelectItem value="any">{t.filters.any}</SelectItem>
              {BODY_TYPES.map((b) => <SelectItem key={b} value={b} className="capitalize">{b}</SelectItem>)}
            </SelectContent>
          </Select>
        </FilterField>

        <FilterField label={t.filters.condition}>
          <div className="flex flex-wrap gap-1.5">
            <Pill active={!get("condition")} onClick={() => setMany({ condition: "" })}>{t.cond.any}</Pill>
            {CONDITIONS.map((c) => (
              <Pill key={c} active={get("condition") === c} onClick={() => setMany({ condition: c })} testId={`cond-${c}`}>{t.cond[c] || c}</Pill>
            ))}
          </div>
        </FilterField>

        <FilterField label={t.filters.sellerType}>
          <div className="flex flex-wrap gap-1.5">
            <Pill active={!get("seller_type")} onClick={() => setMany({ seller_type: "" })}>{t.seller.any}</Pill>
            {SELLER_TYPES.map((s) => (
              <Pill key={s} active={get("seller_type") === s} onClick={() => setMany({ seller_type: s })} testId={`seller-${s}`}>{t.seller[s] || s}</Pill>
            ))}
          </div>
          {/* Phase 65 — Verified-Dealer toggle */}
          <label className="mt-3 flex items-center gap-2 text-xs text-zinc-300 cursor-pointer select-none" data-testid="filter-verified-dealer-label">
            <input
              type="checkbox"
              checked={get("verified_dealer") === "true"}
              onChange={(e) => setMany({ verified_dealer: e.target.checked ? "true" : "" })}
              className="h-4 w-4 rounded border-zinc-700 bg-zinc-900 text-red-500 focus:ring-red-500"
              data-testid="filter-verified-dealer"
            />
            <span className="inline-flex items-center gap-1">
              <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-red-600/15 text-red-400" aria-hidden="true">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
              </span>
              Nur verifizierte Händler
            </span>
          </label>
        </FilterField>

        {/* Phase 68.3 — Trust & quality filters */}
        <FilterField label="Qualität & Vertrauen">
          <div className="grid grid-cols-2 gap-2" data-testid="trust-filters">
            {[
              { key: "ab_mfk", label: "Ab MFK" },
              { key: "accident_free", label: "Unfallfrei" },
              { key: "warranty_available", label: "Garantie" },
              { key: "service_book", label: "Service-Heft" },
            ].map((tf) => {
              const checked = get(tf.key) === "1";
              return (
                <button
                  key={tf.key}
                  type="button"
                  onClick={() => setMany({ [tf.key]: checked ? "" : "1" })}
                  data-testid={`filter-${tf.key}`}
                  aria-pressed={checked}
                  className={`flex min-h-[40px] items-center gap-2 rounded-lg border px-3 py-2 text-left text-xs transition-colors ${
                    checked
                      ? "border-red-500/60 bg-red-600/10 text-white"
                      : "border-zinc-800 bg-zinc-950 text-zinc-300 hover:border-zinc-700 hover:text-white"
                  }`}
                >
                  <span className={`grid h-4 w-4 flex-shrink-0 place-items-center rounded border ${checked ? "border-red-500 bg-red-600 text-white" : "border-zinc-700 bg-zinc-950"}`}>
                    {checked && <Check className="h-3 w-3" strokeWidth={3} />}
                  </span>
                  <span className="leading-tight">{tf.label}</span>
                </button>
              );
            })}
          </div>
        </FilterField>

        <FilterField label={t.filters.price}>
          <div className="flex gap-2">
            <Input type="number" placeholder={t.filters.min} value={get("min_price")} onChange={(e) => setMany({ min_price: e.target.value })} className="border-[var(--ch-line)] bg-[var(--ch-bg-1)] text-white" data-testid="filter-min-price" />
            <Input type="number" placeholder={t.filters.max} value={get("max_price")} onChange={(e) => setMany({ max_price: e.target.value })} className="border-[var(--ch-line)] bg-[var(--ch-bg-1)] text-white" data-testid="filter-max-price" />
          </div>
        </FilterField>

        <FilterField label={t.filters.km}>
          <Input type="number" value={get("max_km")} onChange={(e) => setMany({ max_km: e.target.value })} className="border-[var(--ch-line)] bg-[var(--ch-bg-1)] text-white" data-testid="filter-max-km" />
        </FilterField>

        <FilterField label={t.filters.year}>
          <Input type="number" value={get("min_year")} onChange={(e) => setMany({ min_year: e.target.value })} className="border-[var(--ch-line)] bg-[var(--ch-bg-1)] text-white" data-testid="filter-min-year" />
        </FilterField>

        <FilterField label={t.filters.warrantyMonths}>
          <Input type="number" value={get("min_warranty")} onChange={(e) => setMany({ min_warranty: e.target.value })} placeholder="0" className="border-[var(--ch-line)] bg-[var(--ch-bg-1)] text-white" data-testid="filter-min-warranty" />
        </FilterField>
      </Section>

      {/* Engine & drivetrain */}
      <Section title={t.filters.engine}>
        <FilterField label={t.filters.fuel}>
          <Select value={get("fuel") || "any"} onValueChange={(v) => setMany({ fuel: v === "any" ? "" : v })}>
            <SelectTrigger data-testid="filter-fuel" className="border-[var(--ch-line)] bg-[var(--ch-bg-1)] capitalize text-white"><SelectValue /></SelectTrigger>
            <SelectContent className="border-[var(--ch-line)] bg-[var(--ch-bg-1)] text-white">
              <SelectItem value="any">{t.filters.any}</SelectItem>
              {FUELS.map((f) => <SelectItem key={f} value={f} className="capitalize">{f}</SelectItem>)}
            </SelectContent>
          </Select>
        </FilterField>

        <FilterField label={t.filters.trans}>
          <Select value={get("transmission") || "any"} onValueChange={(v) => setMany({ transmission: v === "any" ? "" : v })}>
            <SelectTrigger data-testid="filter-transmission" className="border-[var(--ch-line)] bg-[var(--ch-bg-1)] capitalize text-white"><SelectValue /></SelectTrigger>
            <SelectContent className="border-[var(--ch-line)] bg-[var(--ch-bg-1)] text-white">
              <SelectItem value="any">{t.filters.any}</SelectItem>
              {TRANSMISSIONS.map((tr) => <SelectItem key={tr} value={tr} className="capitalize">{tr}</SelectItem>)}
            </SelectContent>
          </Select>
        </FilterField>

        <FilterField label={t.filters.drivetrain}>
          <Select value={get("drivetrain") || "any"} onValueChange={(v) => setMany({ drivetrain: v === "any" ? "" : v })}>
            <SelectTrigger className="border-[var(--ch-line)] bg-[var(--ch-bg-1)] capitalize text-white" data-testid="filter-drivetrain"><SelectValue /></SelectTrigger>
            <SelectContent className="border-[var(--ch-line)] bg-[var(--ch-bg-1)] text-white">
              <SelectItem value="any">{t.filters.any}</SelectItem>
              {DRIVETRAINS.map((d) => <SelectItem key={d} value={d} className="capitalize">{d}</SelectItem>)}
            </SelectContent>
          </Select>
        </FilterField>

        <FilterField label={`${t.filters.power} (PS / kW)`}>
          <div className="flex gap-2">
            <Input type="number" placeholder={t.filters.min} value={get("min_power_hp")} onChange={(e) => setMany({ min_power_hp: e.target.value })} className="border-[var(--ch-line)] bg-[var(--ch-bg-1)] text-white" data-testid="filter-min-power" />
            <Input type="number" placeholder={t.filters.max} value={get("max_power_hp")} onChange={(e) => setMany({ max_power_hp: e.target.value })} className="border-[var(--ch-line)] bg-[var(--ch-bg-1)] text-white" data-testid="filter-max-power" />
          </div>
          <PowerHint min={get("min_power_hp")} max={get("max_power_hp")} />
        </FilterField>

        <FilterField label={t.filters.displacement}>
          <div className="flex gap-2">
            <Input type="number" placeholder={t.filters.min} value={get("min_displacement")} onChange={(e) => setMany({ min_displacement: e.target.value })} className="border-[var(--ch-line)] bg-[var(--ch-bg-1)] text-white" data-testid="filter-min-displacement" />
            <Input type="number" placeholder={t.filters.max} value={get("max_displacement")} onChange={(e) => setMany({ max_displacement: e.target.value })} className="border-[var(--ch-line)] bg-[var(--ch-bg-1)] text-white" data-testid="filter-max-displacement" />
          </div>
        </FilterField>

        <FilterField label={t.filters.cylinders}>
          <div className="flex flex-wrap gap-1.5">
            <Pill active={!get("cylinders")} onClick={() => setMany({ cylinders: "" })}>{t.filters.any}</Pill>
            {CYLINDER_OPTIONS.map((c) => (
              <Pill key={c} active={get("cylinders") === String(c)} onClick={() => setMany({ cylinders: String(c) })} testId={`cyl-${c}`}>{c}</Pill>
            ))}
          </div>
        </FilterField>

        <FilterField label={t.filters.euroNorm}>
          <Select value={get("euro_norm") || "any"} onValueChange={(v) => setMany({ euro_norm: v === "any" ? "" : v })}>
            <SelectTrigger className="border-[var(--ch-line)] bg-[var(--ch-bg-1)] text-white" data-testid="filter-euro"><SelectValue /></SelectTrigger>
            <SelectContent className="border-[var(--ch-line)] bg-[var(--ch-bg-1)] text-white">
              <SelectItem value="any">{t.filters.any}</SelectItem>
              {EURO_NORMS.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
        </FilterField>
      </Section>

      {/* Electric & efficiency */}
      <Section title={t.filters.electric}>
        <FilterField label={`${t.filters.consumption} (max)`}>
          <Input type="number" step="0.1" value={get("max_fuel_consumption")} onChange={(e) => setMany({ max_fuel_consumption: e.target.value })} placeholder="z.B. 6.5" className="border-[var(--ch-line)] bg-[var(--ch-bg-1)] text-white" data-testid="filter-max-consumption" />
        </FilterField>
        <FilterField label={`${t.filters.co2} (max)`}>
          <Input type="number" value={get("max_co2")} onChange={(e) => setMany({ max_co2: e.target.value })} placeholder="z.B. 120" className="border-[var(--ch-line)] bg-[var(--ch-bg-1)] text-white" data-testid="filter-max-co2" />
        </FilterField>
        <FilterField label={`${t.filters.electricRange} (min)`}>
          <Input type="number" value={get("min_electric_range")} onChange={(e) => setMany({ min_electric_range: e.target.value })} placeholder="z.B. 300" className="border-[var(--ch-line)] bg-[var(--ch-bg-1)] text-white" data-testid="filter-min-range" />
        </FilterField>
        <FilterField label={`${t.filters.battery} (min)`}>
          <Input type="number" step="0.1" value={get("min_battery")} onChange={(e) => setMany({ min_battery: e.target.value })} placeholder="z.B. 60" className="border-[var(--ch-line)] bg-[var(--ch-bg-1)] text-white" data-testid="filter-min-battery" />
        </FilterField>
      </Section>

      {/* Colors */}
      <Section title={t.filters.colors}>
        <FilterField label={t.filters.exteriorColor}>
          <ColorSwatchRow options={EXTERIOR_COLORS} active={get("exterior_color")} onPick={(v) => setMany({ exterior_color: v })} testIdPrefix="ext" />
        </FilterField>
        <FilterField label={t.filters.interiorColor}>
          <ColorSwatchRow options={INTERIOR_COLORS} active={get("interior_color")} onPick={(v) => setMany({ interior_color: v })} testIdPrefix="int" />
        </FilterField>
        <FilterField label={t.filters.metallic}>
          <ToggleRow
            value={get("metallic")}
            onChange={(v) => setMany({ metallic: v })}
            options={[
              { v: "", label: t.filters.any },
              { v: "1", label: t.filters.metallic },
            ]}
            testIdPrefix="metallic"
          />
        </FilterField>
      </Section>

      {/* Doors / seats */}
      <Section title={`${t.filters.doors} / ${t.filters.seats}`}>
        <FilterField label={t.filters.doors}>
          <div className="flex flex-wrap gap-1.5">
            <Pill active={!get("doors")} onClick={() => setMany({ doors: "" })}>{t.filters.any}</Pill>
            {DOOR_OPTIONS.map((d) => (
              <Pill key={d} active={get("doors") === String(d)} onClick={() => setMany({ doors: String(d) })} testId={`doors-${d}`}>{d}</Pill>
            ))}
          </div>
        </FilterField>
        <FilterField label={t.filters.seats}>
          <div className="flex flex-wrap gap-1.5">
            <Pill active={!get("seats")} onClick={() => setMany({ seats: "" })}>{t.filters.any}</Pill>
            {SEAT_OPTIONS.map((s) => (
              <Pill key={s} active={get("seats") === String(s)} onClick={() => setMany({ seats: String(s) })} testId={`seats-${s}`}>{s}</Pill>
            ))}
          </div>
        </FilterField>
      </Section>

      {/* Equipment */}
      <Section title={`${t.filters.equipment}${selectedFeatures.length ? ` · ${selectedFeatures.length}` : ""}`}>
        <div className="space-y-2">
          {FEATURE_CATEGORIES.map((cat) => {
            const localCount = cat.items.filter((i) => selectedFeatures.includes(i)).length;
            return (
              <details key={cat.id} className="group rounded-md border border-[var(--ch-line)] bg-[var(--ch-bg-1)]">
                <summary className="flex cursor-pointer list-none items-center justify-between px-3 py-2 text-xs font-semibold uppercase tracking-wide text-zinc-300 transition-colors hover:text-white">
                  <span className="flex items-center gap-2">
                    {cat.label.de}
                    {localCount > 0 && <span className="rounded-full bg-[var(--ch-red)] px-1.5 py-0.5 text-[10px] font-bold text-white">{localCount}</span>}
                  </span>
                  <ChevronDown className="h-4 w-4 text-zinc-500 transition-transform group-open:rotate-180" />
                </summary>
                <div className="grid grid-cols-1 gap-1 border-t border-[var(--ch-line-soft)] px-3 py-2 sm:grid-cols-2">
                  {cat.items.map((item) => {
                    const active = selectedFeatures.includes(item);
                    return (
                      <button
                        type="button"
                        key={item}
                        onClick={() => toggleFeature(item)}
                        data-testid={`filter-feature-${item}`}
                        className={`flex w-full items-center gap-2 rounded px-1 py-1.5 text-left text-xs transition-colors ${active ? "text-white" : "text-zinc-400 hover:text-white"}`}
                      >
                        <span className={`grid h-4 w-4 flex-shrink-0 place-items-center rounded-sm border transition-colors ${active ? "border-[var(--ch-red)] bg-[var(--ch-red)]" : "border-zinc-700 bg-transparent"}`}>
                          {active && <Check className="h-3 w-3 text-white" />}
                        </span>
                        <span className="leading-tight">{item}</span>
                      </button>
                    );
                  })}
                </div>
              </details>
            );
          })}
        </div>
      </Section>
    </div>
  );

  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
      <ListingsSeo total={total} items={items} locale={locale} t={t} />
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-white sm:text-4xl">{t.nav.browse}</h1>
          <p className="mt-2 text-sm text-zinc-400">{total} {total === 1 ? "Inserat" : "Inserate"}</p>
        </div>

        {/* Mobile filter drawer */}
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" className="lg:hidden border-[var(--ch-line)] bg-[var(--ch-bg-1)] text-white hover:bg-[var(--ch-bg-2)]" data-testid="mobile-filters-trigger">
              <SlidersHorizontal className="mr-2 h-4 w-4" /> {t.filters.title}
              {chips.length > 0 && (
                <span className="ml-2 rounded-full bg-[var(--ch-red)] px-2 py-0.5 text-[10px] font-bold text-white">{chips.length}</span>
              )}
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[92vw] max-w-md border-[var(--ch-line)] bg-[var(--ch-bg-0)] p-0 text-white sm:w-[420px]">
            <div className="flex h-full flex-col">
              <SheetHeader className="border-b border-[var(--ch-line)] px-5 py-4">
                <SheetTitle className="text-white">{t.filters.title}</SheetTitle>
              </SheetHeader>
              <div className="flex-1 overflow-y-auto px-5 pb-2 pt-4">{filtersBlock}</div>
              {/* Sticky CTA — Phase 68.3 fixed via controlled state */}
              <div className="sticky bottom-0 border-t border-[var(--ch-line)] bg-[var(--ch-bg-0)]/95 px-5 py-4 backdrop-blur-md" style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}>
                <Button
                  className="w-full bg-[var(--ch-red)] text-white hover:bg-[var(--ch-red)]/90"
                  size="lg"
                  data-testid="mobile-show-results"
                  onClick={() => setMobileOpen(false)}
                >
                  {t.filters.showResults} · {total}
                </Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      {/* Phase 68.4 — Inline quick-filter popovers (AutoScout24-style, mobile-only)
          Phase 68.5 — "Mehr Filter" chip removed; the single "Filter" button
          in the page header is the only drawer trigger. */}
      <MobileQuickFilters
        get={get}
        setMany={setMany}
        total={total}
        chipsCount={chips.length}
        onSaveSearch={user ? handleSaveSearch : null}
        saveSearchBusy={savingSearch}
      />

      {/* Active filter chips */}
      {chips.length > 0 && (
        <div className="mt-6 flex flex-wrap gap-2" data-testid="filter-chips">
          {chips.map((c) => (
            <button
              key={c.k}
              onClick={() => c.clear ? c.clear() : setMany({ [c.k]: "" })}
              className="inline-flex items-center gap-1.5 rounded-full border border-[var(--ch-line)] bg-[var(--ch-bg-1)] px-3 py-1 text-xs text-zinc-200 transition-colors hover:border-[var(--ch-red)]/50 hover:text-white"
            >
              {c.label} <X className="h-3 w-3" />
            </button>
          ))}
          <button onClick={() => setParams({})} className="text-xs text-zinc-500 hover:text-white" data-testid="reset-all-chips">{t.filters.reset}</button>
        </div>
      )}

      <div className="mt-8 grid gap-10 lg:grid-cols-[300px_1fr]">
        {/* Desktop sticky filters */}
        <aside className="hidden lg:sticky lg:top-20 lg:block lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto lg:self-start lg:pr-1">
          {filtersBlock}
        </aside>

        {/* Results */}
        <div>
          <div className="mb-6 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
            <Input
              placeholder={t.filters.search}
              defaultValue={get("q")}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const q = e.target.value || "";
                  setMany({ q });
                  trackEvent("search_used", { query: q.slice(0, 80), brand: get("brand"), canton: get("canton") });
                }
              }}
              className="max-w-md border-[var(--ch-line)] bg-[var(--ch-bg-1)] text-white"
              data-testid="search-input"
            />
            <div className="flex flex-wrap items-center gap-2">
              {user && Object.keys(buildQuery()).length > 0 && (
                <Button
                  variant="outline" size="sm" disabled={savingSearch}
                  onClick={handleSaveSearch}
                  className="border-[var(--ch-line)] bg-transparent text-white hover:bg-[var(--ch-bg-2)]"
                  data-testid="save-search-button"
                >
                  <BellPlus className="mr-2 h-4 w-4" /> Alert
                </Button>
              )}
              <Select value={get("sort") || "newest"} onValueChange={(v) => setMany({ sort: v })}>
                <SelectTrigger className="w-56 border-[var(--ch-line)] bg-[var(--ch-bg-1)] text-white" data-testid="sort-select"><SelectValue /></SelectTrigger>
                <SelectContent className="border-[var(--ch-line)] bg-[var(--ch-bg-1)] text-white">
                  <SelectItem value="newest">{t.filters.newest}</SelectItem>
                  <SelectItem value="price_asc">{t.filters.priceAsc}</SelectItem>
                  <SelectItem value="price_desc">{t.filters.priceDesc}</SelectItem>
                  <SelectItem value="km_asc">{t.filters.kmAsc}</SelectItem>
                  <SelectItem value="year_desc">{t.filters.yearDesc}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (<div key={i} className="aspect-[16/12] animate-pulse rounded-2xl border border-[var(--ch-line)] bg-[var(--ch-bg-1)]" />))}
            </div>
          ) : items.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[var(--ch-line)] p-16 text-center text-zinc-500" data-testid="no-results">{t.misc.noResults}</div>
          ) : (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3" data-testid="listing-grid">
              {items.map((c) => <CarCard key={c.id} car={c} />)}
            </div>
          )}

          {/* Recently Viewed — at the bottom so it doesn't compete with active search */}
          <RecentlyViewed limit={6} showViewAllLink={false} />
        </div>
      </div>
    </div>
  );
}

/* ---------- Sub-components ---------- */

function Section({ title, children, defaultOpen = false }) {
  return (
    <details open={defaultOpen} className="group border-b border-[var(--ch-line)] py-3">
      <summary className="flex cursor-pointer list-none items-center justify-between py-1 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-300 transition-colors hover:text-white">
        <span>{title}</span>
        <ChevronDown className="h-4 w-4 text-zinc-500 transition-transform group-open:rotate-180" />
      </summary>
      <div className="mt-4 space-y-5">{children}</div>
    </details>
  );
}

function FilterField({ label, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-400">{label}</Label>
      {children}
    </div>
  );
}

function Pill({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`rounded-full border px-3 py-1 text-xs capitalize transition-colors ${
        active
          ? "border-[var(--ch-red)] bg-[var(--ch-red)]/15 text-white"
          : "border-[var(--ch-line)] bg-[var(--ch-bg-1)] text-zinc-400 hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}

function ColorSwatchRow({ options, active, onPick, testIdPrefix }) {
  return (
    <div className="flex flex-wrap gap-2">
      <button
        type="button"
        onClick={() => onPick("")}
        data-testid={`${testIdPrefix}-color-any`}
        className={`grid h-8 w-8 place-items-center rounded-full border text-[10px] uppercase transition-colors ${
          !active ? "border-[var(--ch-red)] text-white" : "border-[var(--ch-line)] text-zinc-500 hover:text-white"
        }`}
        title="Alle"
      >
        ∗
      </button>
      {options.map((c) => {
        const isActive = active === c.value;
        return (
          <button
            key={c.value}
            type="button"
            onClick={() => onPick(c.value)}
            title={c.label}
            data-testid={`${testIdPrefix}-color-${c.value}`}
            className={`relative h-8 w-8 rounded-full border-2 transition-transform hover:scale-110 ${
              isActive ? "border-[var(--ch-red)] ring-2 ring-[var(--ch-red)]/40 ring-offset-2 ring-offset-[var(--ch-bg-0)]" : "border-zinc-800"
            }`}
            style={{ backgroundColor: c.hex }}
          >
            {isActive && (
              <Check className={`absolute inset-0 m-auto h-4 w-4 ${c.hex === "#f4f4f5" || c.hex === "#cdb89a" || c.hex === "#facc15" ? "text-black" : "text-white"}`} />
            )}
          </button>
        );
      })}
    </div>
  );
}

function ToggleRow({ value, onChange, options, testIdPrefix }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => (
        <Pill key={o.v || "any"} active={value === o.v} onClick={() => onChange(o.v)} testId={`${testIdPrefix}-${o.v || "any"}`}>
          {o.label}
        </Pill>
      ))}
    </div>
  );
}

function PowerHint({ min, max }) {
  const fmt = (hp) => hp ? `${hp} PS · ${Math.round(Number(hp) * 0.7355)} kW` : "—";
  if (!min && !max) return null;
  return (
    <p className="text-[10px] text-zinc-500" data-testid="power-hint">
      {fmt(min)} → {fmt(max)}
    </p>
  );
}


/**
 * Phase 63 — Listings page SEO head + JSON-LD (CollectionPage, BreadcrumbList, ItemList).
 */
function ListingsSeo({ total, items, locale, t }) {
  const seo = getPageSeo("listings", locale);
  const canonical = `${SITE_URL}/${locale}/listings`;
  useSeoHead({
    title: seo.title,
    description: seo.description,
    canonical,
    image: `${SITE_URL}/icons/og-default.png`,
    hreflangs: buildHreflangs(SITE_URL, "/listings"),
  });

  const breadcrumbs = buildBreadcrumbList([
    { name: "CarHive", url: `${SITE_URL}/${locale}` },
    { name: t.nav.browse, url: canonical },
  ]);

  const collection = buildCollectionPageSchema({
    url: canonical,
    name: seo.title,
    description: seo.description,
    numberOfItems: total || 0,
  });

  const itemList = buildSeoListingsItemList({
    siteUrl: SITE_URL,
    locale,
    listings: (items || []).slice(0, 12),
    fileBaseUrl: FILE_BASE_URL,
    listName: seo.title,
  });
  // Phase 63 — patch ItemList with total count so Google sees collection size
  if (itemList && typeof total === "number") {
    itemList.numberOfItems = total;
  }

  return (
    <>
      {breadcrumbs && <JsonLd id="listings-breadcrumbs" data={breadcrumbs} />}
      {collection && <JsonLd id="listings-collection" data={collection} />}
      {itemList && <JsonLd id="listings-itemlist" data={itemList} />}
    </>
  );
}
